import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Wand2 } from 'lucide-react';
import Modal from './Modal';
import { api } from '../../lib/api';
import {
  AUTO_RESOLVE_BATCH_ACTIONS, AUTO_RESOLVE_BATCH_BUCKETS, AUTO_RESOLVE_BATCH_MAX,
} from '../../config/autoResolve';
import { BucketChip } from './AutoResolveBatchCard';
import {
  parseWhen, plural, problemsText, SECTION_HEADING, PRIMARY_BTN, ERROR_BOX, errorText, isDisabledResponse,
} from './autoResolveBatchShared';

// "Run batch auto-resolve" (docs/auto-resolve-batch.md §3, EXPERIMENTAL): the
// Edit Requests bulk action and the header button open this with a set of
// list rows. It groups them by school — one batch per school, at most 12
// requests (the oldest), Person requests left out with their reason — checks
// each school's readiness (GET /api/admin/accounts/:id/auto-resolve-batch),
// and on Run posts { action: 'dispatch', requestIds } to every ready school,
// one after another, showing each school's outcome. Rows already in a batch,
// resolved or closed, and Visual Editor updates are ignored with a note.

const { HUMAN } = AUTO_RESOLVE_BATCH_BUCKETS;

function schoolEndpoint(accountId) {
  return `/api/admin/accounts/${encodeURIComponent(accountId || '')}/auto-resolve-batch`;
}

function oldestFirst(a, b) {
  return (parseWhen(a?.createdAt)?.getTime() ?? 0) - (parseWhen(b?.createdAt)?.getTime() ?? 0);
}

function requestLabel(row) {
  if (row?.title) return row.title;
  const d = String(row?.description || '').replace(/\s+/g, ' ').trim();
  if (d) return d.length > 80 ? `${d.slice(0, 78)}…` : d;
  return `Request on ${row?.pagePath || 'the site'}`;
}

const IGNORED_TEXT = {
  batched: (n) => `${n} already in a batch`,
  closed: (n) => `${n} resolved or closed`,
  inline: (n) => `${plural(n, 'Visual Editor update')}`,
  unclassified: (n) => `${n} that can’t be batched`,
};

// Groups list rows into one plan per school. Pure; exported for tests.
//   ignored:  [{ row, why: 'batched' | 'closed' | 'inline' | 'unclassified' }]
//   schools:  [{ accountId, accountName, sendable: [row] (oldest first), person: [row] }]
export function groupBatchRequests(rows) {
  const ignored = [];
  const bySchool = new Map();
  for (const row of rows || []) {
    if (!row || row.id == null) continue;
    const ar = row.autoResolve;
    if (!ar) {
      const stored = row.storedStatus || row.status;
      const why = row.source === 'inline' ? 'inline' : ['resolved', 'closed'].includes(stored) ? 'closed' : 'unclassified';
      ignored.push({ row, why });
      continue;
    }
    if (ar.locked) {
      ignored.push({ row, why: 'batched' });
      continue;
    }
    const key = String(row.accountId || '');
    if (!bySchool.has(key)) {
      bySchool.set(key, { accountId: row.accountId, accountName: row.accountName || 'Unnamed school', sendable: [], person: [] });
    }
    const school = bySchool.get(key);
    if (ar.bucket === HUMAN) school.person.push(row); else school.sendable.push(row);
  }
  const schools = [...bySchool.values()]
    .map((s) => ({ ...s, sendable: [...s.sendable].sort(oldestFirst) }))
    .sort((a, b) => a.accountName.localeCompare(b.accountName, undefined, { sensitivity: 'base' }));
  return { ignored, schools };
}

// One school's batch, given what its endpoint said: requests no longer among
// its candidates drop out ("stale"), the oldest `max` are sent, the rest wait
// for the next batch.
export function schoolBatch(school, check, max = AUTO_RESOLVE_BATCH_MAX) {
  const candidateIds = check?.candidateIds || null;
  const stale = candidateIds ? school.sendable.filter((r) => !candidateIds.has(String(r.id))) : [];
  const fresh = candidateIds ? school.sendable.filter((r) => candidateIds.has(String(r.id))) : school.sendable;
  const included = fresh.slice(0, max);
  const overCap = fresh.slice(max);
  const state = school.sendable.length === 0 ? 'nothing' : (check?.state || 'checking');
  return { included, overCap, stale, state, ready: state === 'ready' && included.length > 0 };
}

export default function AutoResolveBatchDialog({ open, rows, onClose, onDispatched }) {
  const plan = useMemo(() => groupBatchRequests(rows), [rows]);
  // accountId → { state: 'checking' | 'ready' | 'not_ready' | 'error', problems, message, candidateIds, repo }
  const [checks, setChecks] = useState({});
  // accountId → { state: 'running' | 'done' | 'failed', message, count }
  const [outcomes, setOutcomes] = useState({});
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);

  // Each school's readiness, checked when the dialog opens.
  useEffect(() => {
    if (!open) return undefined;
    const live = { cancelled: false };
    setOutcomes({});
    setRunning(false);
    setFinished(false);
    const ids = plan.schools.filter((s) => s.sendable.length > 0).map((s) => String(s.accountId));
    setChecks(Object.fromEntries(ids.map((id) => [id, { state: 'checking' }])));
    const settle = (id, check) => {
      if (!live.cancelled) setChecks((prev) => ({ ...prev, [id]: check }));
    };
    ids.forEach((id) => {
      api.get(schoolEndpoint(id)).then((res) => {
        // What the school's endpoint would accept right now: its candidates
        // minus the ones it classifies for a person (a dispatch with those fails).
        const candidateIds = Array.isArray(res?.candidates)
          ? new Set(res.candidates.filter((c) => c && c.classification !== HUMAN).map((c) => String(c.id)))
          : null;
        const repo = typeof res?.repo === 'string' ? res.repo : null;
        settle(id, res?.enabled === false
          ? { state: 'not_ready', problems: Array.isArray(res?.problems) ? res.problems : [], candidateIds, repo }
          : { state: 'ready', candidateIds, repo });
      }, (err) => {
        settle(id, isDisabledResponse(err)
          ? { state: 'not_ready', message: 'batch mode is switched off' }
          : { state: 'error', message: errorText(err, 'Couldn’t check this school.') });
      });
    });
    return () => { live.cancelled = true; };
  }, [open, plan]);

  const views = plan.schools.map((s) => ({ school: s, ...schoolBatch(s, checks[String(s.accountId)]) }));
  const blocked = (v) => v.state === 'not_ready' || v.state === 'error';
  const checking = views.some((v) => v.state === 'checking');
  const ready = views.filter((v) => v.ready);
  const sendCount = views.filter((v) => !blocked(v)).reduce((n, v) => n + v.included.length, 0);
  const leftOutCount = views.reduce(
    (n, v) => n + v.school.person.length + v.overCap.length + v.stale.length + (blocked(v) ? v.included.length : 0),
    0,
  );
  const canRun = !checking && !running && !finished && ready.length > 0;

  const ignoredCounts = plan.ignored.reduce((acc, { why }) => ({ ...acc, [why]: (acc[why] || 0) + 1 }), {});
  const ignoredNote = plan.ignored.length
    ? `${plural(plan.ignored.length, 'selected request is', 'selected requests are')} ignored: ${
      ['batched', 'closed', 'inline', 'unclassified'].filter((k) => ignoredCounts[k]).map((k) => IGNORED_TEXT[k](ignoredCounts[k])).join(', ')}.`
    : '';

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    const results = [];
    for (const v of ready) {
      const id = String(v.school.accountId);
      const requestIds = v.included.map((r) => r.id);
      setOutcomes((prev) => ({ ...prev, [id]: { state: 'running' } }));
      try {
        await api.post(schoolEndpoint(id), { action: AUTO_RESOLVE_BATCH_ACTIONS.DISPATCH, requestIds });
        results.push({ accountId: id, ok: true, requestIds });
        setOutcomes((prev) => ({ ...prev, [id]: { state: 'done', count: requestIds.length } }));
      } catch (err) {
        results.push({ accountId: id, ok: false, requestIds });
        setOutcomes((prev) => ({ ...prev, [id]: { state: 'failed', message: errorText(err, 'Couldn’t start this batch.') } }));
      }
    }
    setRunning(false);
    setFinished(true);
    const started = results.filter((r) => r.ok);
    const failed = results.filter((r) => !r.ok);
    onDispatched?.({
      started: started.length,
      failed: failed.length,
      requestIds: started.flatMap((r) => r.requestIds),
    });
    // Every school started: done. Otherwise stay open on the failures.
    if (!failed.length) onClose?.();
  };

  const close = running ? () => {} : onClose;
  const summary = `${plural(plan.schools.length, 'school')} · ${plural(sendCount, 'request')} · ${leftOutCount} left out`;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Run batch auto-resolve"
      maxWidth="max-w-2xl"
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[12px] text-gray-400" data-testid="batch-dialog-summary">{summary}</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={close}
              disabled={running}
              className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
            >
              {finished ? 'Close' : 'Cancel'}
            </button>
            {!finished && (
              <button type="button" onClick={run} disabled={!canRun} className={PRIMARY_BTN} data-testid="batch-dialog-run">
                {running
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Starting…</>
                  : <><Wand2 className="w-3.5 h-3.5" aria-hidden="true" /> {`Run ${plural(ready.length, 'batch', 'batches')}`}</>}
              </button>
            )}
          </div>
        </div>
      )}
    >
      <div className="flex flex-col gap-4" data-testid="batch-dialog">
        <p className="text-[12px] text-gray-400 leading-relaxed">
          Each school’s requests go to one agent run on a copy of that school’s website, at most{' '}
          {AUTO_RESOLVE_BATCH_MAX} per school (the oldest first). You review each school’s draft change with a preview,
          then publish it or throw it away. Nothing goes live without you.
        </p>

        {ignoredNote && (
          <p className="px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06] text-[12px] text-gray-400" data-testid="batch-dialog-ignored">
            {ignoredNote}
          </p>
        )}

        {plan.schools.length === 0 ? (
          <p className="text-[12px] text-gray-500" data-testid="batch-dialog-empty">
            Nothing to send: none of these requests can go into a batch right now.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {views.map((v) => (
              <SchoolCard key={String(v.school.accountId)} view={v} check={checks[String(v.school.accountId)]} outcome={outcomes[String(v.school.accountId)]} />
            ))}
          </ul>
        )}

        <div role="status" aria-live="polite" className="sr-only" data-testid="batch-dialog-status">
          {running ? 'Starting batches…' : finished ? `${Object.values(outcomes).filter((o) => o.state === 'done').length} started, ${Object.values(outcomes).filter((o) => o.state === 'failed').length} failed.` : ''}
        </div>
      </div>
    </Modal>
  );
}

function SchoolCard({ view, check, outcome }) {
  const { school, included, overCap, stale, state } = view;
  const blocked = state === 'not_ready' || state === 'error';
  const leftOut = [
    ...school.person.map((row) => ({ row, why: row.autoResolve?.reason || 'Needs a person.' })),
    ...overCap.map((row) => ({ row, why: `Over the ${AUTO_RESOLVE_BATCH_MAX}-request limit for one batch — it goes in the next one.` })),
    ...stale.map((row) => ({ row, why: 'Changed since the list loaded: no longer open, already in a batch, or now needs a person.' })),
  ];
  return (
    <li
      className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden"
      data-testid={`batch-school-${school.accountId}`}
      data-state={state}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3.5 pb-3 border-b border-white/[0.06]">
        <h3 className="text-[13px] font-semibold text-white leading-tight break-words">{school.accountName}</h3>
        <Readiness state={state} check={check} />
      </div>

      {/* A school that isn't ready: its requests are inactive (greyed); the
          reason above stays at full contrast. */}
      <div className={`px-4 py-3 flex flex-col gap-3 ${blocked ? 'opacity-60' : ''}`} data-testid={`batch-school-requests-${school.accountId}`}>
        {included.length > 0 && (
          <section>
            <h4 className={`${SECTION_HEADING} mb-1.5`}>{blocked ? 'Would be sent' : 'Will be sent'} ({included.length})</h4>
            <ul className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.04]">
              {included.map((row) => (
                <RequestLine key={row.id} row={row} testId={`batch-send-${row.id}`} />
              ))}
            </ul>
          </section>
        )}
        {leftOut.length > 0 && (
          <section>
            <h4 className={`${SECTION_HEADING} mb-1.5`}>Left out ({leftOut.length})</h4>
            <ul className="rounded-lg border border-white/[0.06] divide-y divide-white/[0.04]">
              {leftOut.map(({ row, why }) => (
                <RequestLine key={row.id} row={row} why={why} testId={`batch-leftout-${row.id}`} />
              ))}
            </ul>
          </section>
        )}
      </div>

      {outcome && (
        <div className="px-4 pb-3" data-testid={`batch-school-outcome-${school.accountId}`}>
          {outcome.state === 'running' && (
            <p className="inline-flex items-center gap-2 text-[12px] text-gray-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Starting…
            </p>
          )}
          {outcome.state === 'done' && (
            <p className="inline-flex items-center gap-2 text-[12px] text-gray-300">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#B0FC38]" aria-hidden="true" /> Batch started ({plural(outcome.count, 'request')}).
            </p>
          )}
          {outcome.state === 'failed' && <div className={ERROR_BOX} role="alert">{outcome.message}</div>}
        </div>
      )}
    </li>
  );
}

function Readiness({ state, check }) {
  if (state === 'nothing') {
    return <span className="text-[11px] text-gray-500" data-testid="batch-school-readiness">Nothing to send</span>;
  }
  if (state === 'checking') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500" data-testid="batch-school-readiness">
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Checking the website setup…
      </span>
    );
  }
  if (state === 'ready') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400" data-testid="batch-school-readiness">
        <CheckCircle2 className="w-3.5 h-3.5 text-[#B0FC38]" aria-hidden="true" /> Ready
        {check?.repo ? <span className="font-mono text-gray-500">{check.repo}</span> : null}
      </span>
    );
  }
  const raw = state === 'error' ? `couldn’t check: ${check?.message || 'unknown error'}` : (check?.message || problemsText(check?.problems));
  const reason = raw.replace(/[.\s]+$/, '');
  return (
    <span className="inline-flex items-start gap-1.5 text-[12px] text-amber-200/90" data-testid="batch-school-readiness">
      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-300" aria-hidden="true" />
      <span>Not ready: {reason}. Not sent.</span>
    </span>
  );
}

function RequestLine({ row, why, testId }) {
  return (
    <li className="flex items-start gap-2 px-3 py-2" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium text-white break-words">{requestLabel(row)}</div>
        <div className="text-[11px] text-gray-500 font-mono break-all">{row.pagePath}</div>
        {why && <p className="mt-0.5 text-[11px] text-gray-400 break-words">{why}</p>}
      </div>
      {row.autoResolve?.bucket && (
        <BucketChip bucket={row.autoResolve.bucket} reason={row.autoResolve.reason} short className="mt-0.5" testId="batch-dialog-chip" />
      )}
    </li>
  );
}
